import type { ChartData, ChartOptions } from "chart.js"
import "chartjs-adapter-luxon"
import { useState } from "react"
import SortOrderToggle, {
  type SortOrder,
} from "../../../components/SortOrderToggle"
import {
  getAnnotationSettings,
  getPersistentColor,
} from "../-components/ChartUtils"
import {
  FeedingStatsPage,
  type FeedingStatsRenderComponentProps,
} from "../-components/FeedingStatsPage"
import GenauigkeitBarChart from "../-components/GenauigkeitBarChart"

/** @note "Genauigkeit" (accuracy) values are centered around a target of 100 %. */
const ACCURACY_TARGET = 100

type NamedSeries = { label: string; yData: number[] }
type BarEntry = { label: string; deviation: number }

function average(values: number[]): number {
  if (values.length === 0) {
    return 0
  }
  return values.reduce((sum, value) => sum + value, 0) / values.length
}

/** One bar per series, valued at the series' average deviation from the 100 % target. */
function toBarEntries(series: NamedSeries[]): BarEntry[] {
  return series.map((s) => ({
    label: s.label,
    deviation: average(s.yData) - ACCURACY_TARGET,
  }))
}

function sortBarEntries(entries: BarEntry[], order: SortOrder): BarEntry[] {
  const sorted = [...entries]
  if (order === "alphabetical") {
    sorted.sort((a, b) => a.label.localeCompare(b.label, "de"))
  } else {
    sorted.sort((a, b) => Math.abs(b.deviation) - Math.abs(a.deviation))
  }
  return sorted
}

function toBarData(entries: BarEntry[]): ChartData<"bar", number[], string> {
  return {
    labels: entries.map((e) => e.label),
    datasets: [
      {
        label: "Ø Abweichung vom Ziel",
        data: entries.map((e) => e.deviation),
        backgroundColor: entries.map((_, index) => getPersistentColor(index)),
      },
    ],
  }
}

function getSymmetricMax(chartData: ChartData<"bar", number[], string>) {
  const values = chartData.datasets[0]?.data ?? []
  const maxAbs = values.reduce(
    (max, value) => Math.max(max, Math.abs(value)),
    0
  )
  return maxAbs === 0 ? 1 : maxAbs
}

function buildBarOptions(maxAbsDeviation: number): ChartOptions<"bar"> {
  const annotationSettings = getAnnotationSettings()
  const axisMax = maxAbsDeviation * 1.1

  return {
    indexAxis: "y" as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const rounded = Math.round(Number(context.parsed.x))
            const sign = rounded > 0 ? "+" : ""
            return `${context.dataset.label}: ${sign}${rounded}%`
          },
        },
      },
      annotation: {
        annotations: {
          boxYellowUpper: {
            type: "box",
            xMin: annotationSettings.yellowUpper.min - ACCURACY_TARGET,
            xMax: annotationSettings.yellowUpper.max - ACCURACY_TARGET,
            backgroundColor: annotationSettings.yellowUpper.color,
            borderWidth: 0,
          },
          boxGreen: {
            type: "box",
            xMin: annotationSettings.green.min - ACCURACY_TARGET,
            xMax: annotationSettings.green.max - ACCURACY_TARGET,
            backgroundColor: annotationSettings.green.color,
            borderWidth: 0,
          },
          boxYellowLower: {
            type: "box",
            xMin: annotationSettings.yellowLower.min - ACCURACY_TARGET,
            xMax: annotationSettings.yellowLower.max - ACCURACY_TARGET,
            backgroundColor: annotationSettings.yellowLower.color,
            borderWidth: 0,
          },
        },
      },
    },
    scales: {
      x: {
        min: -axisMax,
        max: axisMax,
        ticks: {
          callback: (value: any) => {
            const rounded = Math.round(Number(value))
            return rounded > 0 ? `+${rounded}%` : `${rounded}%`
          },
        },
      },
      y: {
        ticks: {
          autoSkip: false,
        },
      },
    },
  }
}

export default function GenauigkeitOverall() {
  const title = "Genauigkeit"

  return (
    <FeedingStatsPage
      title={title}
      isDataPresent={(feedingStats: any): boolean =>
        feedingStats &&
        feedingStats.AccuracyPerComponent &&
        feedingStats.AccuracyPerDriver
      }
      RenderComponent={GenauigkeitRenderComponent}
    />
  )
}

function GenauigkeitRenderComponent({
  feedingStats,
}: FeedingStatsRenderComponentProps) {
  const _className = "chart-container"

  const [componentSortOrder, setComponentSortOrder] =
    useState<SortOrder>("alphabetical")
  const [driverSortOrder, setDriverSortOrder] =
    useState<SortOrder>("alphabetical")

  const componentSeries: NamedSeries[] = Object.values(
    feedingStats.AccuracyPerComponent as {
      [feedComponentId: string]: { label: string; yData: number[] }
    }
  ).map((entry) => ({ label: entry.label, yData: entry.yData }))

  const driverStats = feedingStats.AccuracyPerDriver as {
    labels: string[]
    yData: number[][]
  }
  const driverSeries: NamedSeries[] = driverStats.labels.map(
    (label, index) => ({
      label,
      yData: driverStats.yData[index] ?? [],
    })
  )

  const barDataComponents = toBarData(
    sortBarEntries(toBarEntries(componentSeries), componentSortOrder)
  )
  const barDataDrivers = toBarData(
    sortBarEntries(toBarEntries(driverSeries), driverSortOrder)
  )

  return (
    <div className="rm-list-container">
      <div className="mb-2">
        Von <u>{new Date(feedingStats.meta.begin).toLocaleString("de-de")}</u>{" "}
        bis <u>{new Date(feedingStats.meta.end).toLocaleString("de-de")}</u>
      </div>
      <GenauigkeitBarChart
        _className={_className}
        title="Durchschnittliche Genauigkeit der Futter-Komponenten"
        actions={
          <SortOrderToggle
            value={componentSortOrder}
            onChange={setComponentSortOrder}
            magnitudeLabel="Nach Abweichung"
          />
        }
        data={barDataComponents}
        chartOptions={buildBarOptions(getSymmetricMax(barDataComponents))}
      />
      <GenauigkeitBarChart
        _className={_className + " !mt-8"}
        title="Genauigkeit pro Fahrer (schlechteste Komponente)"
        actions={
          <SortOrderToggle
            value={driverSortOrder}
            onChange={setDriverSortOrder}
            magnitudeLabel="Nach Abweichung"
          />
        }
        data={barDataDrivers}
        chartOptions={buildBarOptions(getSymmetricMax(barDataDrivers))}
      />
    </div>
  )
}
